import React, { useState, useEffect, useMemo, useRef, TouchEvent } from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  Grid,
  Divider,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  DialogContentText,
  Menu,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  MoreVert,
} from '@mui/icons-material';
import {
  format,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  startOfDay,
  endOfDay,
  addHours,
  parseISO,
  differenceInMinutes,
  isWithinInterval,
  isSameDay,
  isToday,
  formatISO,
  isBefore,
  isAfter,
  getHours,
  getMinutes,
} from 'date-fns';
import { updateAppointmentStatus, rescheduleAppointment } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

// Function to generate a consistent color based on the customer name
const generateCustomerColor = (name: string) => {
  // Create a hash from the name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Create colors with good saturation and lightness for visibility
  const h = Math.abs(hash) % 360;
  const s = 70 + (Math.abs(hash) % 20); // 70-90% saturation
  const l = 60 + (Math.abs(hash) % 15); // 60-75% lightness
  
  return `hsl(${h}, ${s}%, ${l}%)`;
};

interface Appointment {
  id: number;
  client_name: string;
  service_name: string;
  date: string;
  time: string;
  status: string;
  duration?: number;
}

type AppointmentStatus = 'completed' | 'cancelled';

interface NewAppointmentCalendarProps {
  appointments: Appointment[];
  onStatusUpdate: (appointmentId?: number, status?: string) => void;
}

const NewAppointmentCalendar: React.FC<NewAppointmentCalendarProps> = ({ appointments, onStatusUpdate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<'week' | 'day'>('week');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState<AppointmentStatus | ''>('');
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [rescheduleErrors, setRescheduleErrors] = useState<{date?: string, time?: string}>({});
  const [draggedAppointment, setDraggedAppointment] = useState<Appointment | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropTarget, setDropTarget] = useState<{ date: string; time: string } | null>(null);
  const [dragConfirmOpen, setDragConfirmOpen] = useState(false);
  const timeColumnRef = useRef<HTMLDivElement>(null);
  const dragImageRef = useRef<HTMLDivElement>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchDragging, setTouchDragging] = useState(false);
  const [touchTarget, setTouchTarget] = useState<HTMLElement | null>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [currentTouchColumn, setCurrentTouchColumn] = useState<string | null>(null);
  const daysContainerRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef<{[key: string]: HTMLElement | null}>({});
  const [visibleDays, setVisibleDays] = useState<number>(7); // Default to showing all 7 days
  const [isUserScrolling, setIsUserScrolling] = useState<boolean>(false);
  const [headerScrollRef, setHeaderScrollRef] = useState<HTMLElement | null>(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmallScreen = useMediaQuery('(max-width:400px)');
  const { showNotification } = useNotification();

  // Add state for appointment details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [viewingAppointment, setViewingAppointment] = useState<Appointment | null>(null);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [draggable, setDraggable] = useState(false);

  // Determine number of visible days based on screen size
  useEffect(() => {
    // Always show all 7 days but adjust scale based on screen size
    setVisibleDays(7);
    
    // Handle resize event
    const handleResize = () => {
      setVisibleDays(7); // Always keep 7 days
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Navigation functions
  const goToPreviousWeek = () => {
    setCurrentDate(subWeeks(currentDate, 1));
  };

  const goToNextWeek = () => {
    setCurrentDate(addWeeks(currentDate, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setTimeout(scrollToToday, 100);
  };

  // Menu functions
  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, appointment: Appointment) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedAppointment(appointment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusUpdate = (status: AppointmentStatus) => {
    handleMenuClose();
    setStatusToUpdate(status);
    setConfirmDialogOpen(true);
  };

  const handleRescheduleClick = () => {
    handleMenuClose();
    if (selectedAppointment) {
      setNewDate(selectedAppointment.date);
      setNewTime(selectedAppointment.time);
      setRescheduleDialogOpen(true);
    }
  };

  const handleConfirmStatusUpdate = async () => {
    if (selectedAppointment && statusToUpdate) {
      setLoading(true);
      try {
        await updateAppointmentStatus(selectedAppointment.id, statusToUpdate);
        if (onStatusUpdate) {
          onStatusUpdate(selectedAppointment.id, statusToUpdate);
        }
        if (showNotification) {
          showNotification(`Appointment ${statusToUpdate === 'completed' ? 'completed' : 'cancelled'} successfully`, 'success');
        }
      } catch (error) {
        console.error('Error updating appointment status:', error);
        if (showNotification) {
          showNotification(`Failed to update appointment status`, 'error');
        }
      } finally {
        setLoading(false);
      }
    }
    setConfirmDialogOpen(false);
    setSelectedAppointment(null);
    setStatusToUpdate('');
  };

  const handleCancelStatusUpdate = () => {
    setConfirmDialogOpen(false);
    setSelectedAppointment(null);
    setStatusToUpdate('');
  };

  const validateRescheduleForm = () => {
    let isValid = true;
    const errors: { date?: string, time?: string } = {};

    if (!newDate) {
      errors.date = 'Date is required';
      isValid = false;
    } else {
      // Check if date is in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(newDate);
      if (selectedDate < today) {
        errors.date = 'Cannot schedule for a past date';
        isValid = false;
      }
    }

    if (!newTime) {
      errors.time = 'Time is required';
      isValid = false;
    }

    setRescheduleErrors(errors);

    if (!isValid && showNotification) {
      showNotification('Please fill in all required fields', 'error');
    }

    return isValid;
  };

  const handleConfirmReschedule = async () => {
    if (!validateRescheduleForm() || !selectedAppointment) {
      return;
    }

    setLoading(true);
    try {
      await rescheduleAppointment(selectedAppointment.id, newDate, newTime);
      if (onStatusUpdate) {
        onStatusUpdate(selectedAppointment.id, selectedAppointment.status);
      }
      setRescheduleDialogOpen(false);
      setSelectedAppointment(null);
      
      if (showNotification) {
        showNotification('Appointment rescheduled successfully', 'success');
      }
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      if (showNotification) {
        showNotification('Failed to reschedule appointment', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRescheduleDialogClose = () => {
    setRescheduleDialogOpen(false);
    setRescheduleErrors({});
  };

  // Generate time slots for the select dropdown
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 19; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        const time = `${formattedHour}:${formattedMinute}:00`;
        const displayTime = format(parseISO(`2023-01-01T${time}`), 'h:mm a');
        slots.push({ value: time, label: displayTime });
      }
    }
    return slots;
  };

  // Calculate week data
  const weekData = useMemo(() => {
    // Start the week on Monday (1) instead of Sunday (0)
    const startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
    const endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
    
    const days = [];
    let day = startDate;
    
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    
    return { startDate, endDate, days };
  }, [currentDate]);

  // Group appointments by day
  const appointmentsByDay = useMemo(() => {
    const groupedAppointments: { [key: string]: Appointment[] } = {};
    
    // Initialize days
    weekData.days.forEach(day => {
      groupedAppointments[format(day, 'yyyy-MM-dd')] = [];
    });
    
    // Filter and group appointments for the current week
    appointments.forEach(appointment => {
      const appointmentDate = appointment.date;
      if (appointmentDate in groupedAppointments) {
        groupedAppointments[appointmentDate].push(appointment);
      }
    });
    
    // Sort appointments by time
    Object.keys(groupedAppointments).forEach(date => {
      groupedAppointments[date].sort((a, b) => {
        return a.time.localeCompare(b.time);
      });
    });
    
    return groupedAppointments;
  }, [appointments, weekData]);

  // Calendar time slots from 9 AM to 7 PM
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 9; hour <= 19; hour++) {
      slots.push(`${hour}:00`);
    }
    return slots;
  }, []);

  // Calculate position and height of appointment block
  const getAppointmentStyles = (appointment: Appointment) => {
    const defaultDuration = appointment.duration || 60; // Default to 60 minutes if duration not provided
    
    // Get the start time
    const timeString = appointment.time;
    const [hours, minutes] = timeString.split(':').map(Number);
    
    // Calculate starting position (top) relative to 9 AM (0 minutes)
    const startMinutes = (hours - 9) * 60 + minutes;
    const topPercentage = (startMinutes / (12 * 60)) * 100; // 12 hours (9 AM to 9 PM) in minutes
    
    // Calculate height based on duration
    const heightPercentage = (defaultDuration / (12 * 60)) * 100;
    
    // Calculate proper height based on duration and current time slot size
    // 60 minutes should be the full hour slot, 30 minutes should be half, etc.
    let minHeight = '70px'; // minimum height
    if (defaultDuration <= 30) {
      minHeight = isVerySmallScreen ? '45px' : '55px'; // 30 min appointments
    } else {
      minHeight = isVerySmallScreen ? '90px' : '110px'; // 60+ min appointments
    }
    
    return {
      top: `${topPercentage}%`,
      height: `${heightPercentage}%`,
      minHeight,
      backgroundColor: generateCustomerColor(appointment.client_name),
    };
  };

  // Handle click on appointment to show details
  const handleAppointmentClick = (e: React.MouseEvent, appointment: Appointment) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only show details if not in dragging mode
    if (!isDragging && !touchDragging) {
      setViewingAppointment(appointment);
      setDetailsDialogOpen(true);
    }
  };

  // Handle long press to enable drag
  const handleAppointmentMouseDown = (e: React.MouseEvent, appointment: Appointment) => {
    // Only enable dragging for scheduled appointments
    if (appointment.status !== 'scheduled') return;
    
    // Start a timer for long press
    const timer = setTimeout(() => {
      setDraggable(true);
      // Visual feedback that element is now draggable
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.style.cursor = 'grab';
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
      }
    }, 500); // 500ms long press
    
    setLongPressTimer(timer);
  };

  const handleAppointmentMouseUp = () => {
    // Clear the long press timer if mouse is released before the timeout
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleAppointmentMouseLeave = () => {
    // Clear the long press timer if mouse leaves the element
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Modify drag start to only work if draggable is true
  const handleDragStart = (e: React.DragEvent, appointment: Appointment) => {
    if (!draggable) {
      e.preventDefault();
      return;
    }
    
    e.stopPropagation();
    // Set data for drag operation
    e.dataTransfer.setData('appointmentId', appointment.id.toString());
    
    // Create a custom drag image
    if (dragImageRef.current) {
      dragImageRef.current.innerText = `${appointment.service_name} - ${appointment.client_name}`;
      dragImageRef.current.style.backgroundColor = getAppointmentColor(appointment);
      dragImageRef.current.style.display = 'block';
      
      // Set the drag image
      e.dataTransfer.setDragImage(
        dragImageRef.current,
        dragImageRef.current.offsetWidth / 2,
        dragImageRef.current.offsetHeight / 2
      );
    }
    
    setDraggedAppointment(appointment);
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggable(false);
    
    if (dragImageRef.current) {
      dragImageRef.current.style.display = 'none';
    }
    
    // If no drop target was set, we don't need to show confirmation
    if (!dropTarget) {
      setDraggedAppointment(null);
      return;
    }
    
    setDragConfirmOpen(true);
  };

  // Modify the touch start to implement long press for mobile
  const handleTouchStart = (e: React.TouchEvent, appointment: Appointment, element: HTMLElement, dateKey: string = '') => {
    if (appointment.status !== 'scheduled') return;
    
    // Create a long press timer for touch
    const timer = setTimeout(() => {
      // Only start dragging after long press
      // Prevent default to ensure we capture all subsequent touch events
      e.preventDefault();
      e.stopPropagation();
      
      // Store both X and Y position
      setTouchStartX(e.touches[0].clientX);
      setTouchStartY(e.touches[0].clientY);
      setCurrentTouchColumn(dateKey);
      setDraggedAppointment(appointment);
      setTouchDragging(true);
      setTouchTarget(element);
      
      // Set visual feedback that element is being dragged
      if (element) {
        element.style.boxShadow = '0 8px 16px rgba(0,0,0,0.3)';
        element.style.opacity = '0.8';
        element.style.zIndex = '100';
      }
    }, 500); // 500ms long press
    
    setLongPressTimer(timer);
  };

  const findColumnFromPoint = (x: number, y: number): string | null => {
    if (!daysContainerRef.current) return null;
    
    // Get all columns
    const columns = Object.entries(columnRefs.current);
    
    // Find which column contains the x coordinate
    for (const [dateKey, columnEl] of columns) {
      if (!columnEl) continue;
      
      const rect = columnEl.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right) {
        return dateKey;
      }
    }
    
    return null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault(); // Prevent page scrolling while dragging
    e.stopPropagation();
    
    if (!touchDragging || !draggedAppointment || !daysContainerRef.current) return;
    
    const touchX = e.touches[0].clientX;
    const touchY = e.touches[0].clientY;
    
    // Find which column we're currently over
    const currentColumn = findColumnFromPoint(touchX, touchY);
    
    if (currentColumn) {
      const columnElement = columnRefs.current[currentColumn];
      if (!columnElement) return;
      
      // Update the current column
      setCurrentTouchColumn(currentColumn);
      
      // Calculate the time based on touch position
      const time = calculateTimeFromPosition(touchY, columnElement);
      setDropTarget({ date: currentColumn, time });
      
      // Move the dragged element to follow the touch
      if (touchTarget) {
        // Get the position relative to the entire days container
        const containerRect = daysContainerRef.current.getBoundingClientRect();
        
        // Calculate position (center the element on the touch point)
        const leftPos = touchX - containerRect.left - (touchTarget.offsetWidth / 2);
        const topPos = touchY - containerRect.top;
        
        // Apply positioning
        touchTarget.style.position = 'absolute';
        touchTarget.style.top = `${topPos}px`;
        touchTarget.style.left = `${leftPos}px`;
        touchTarget.style.width = '80px'; // Fixed width to ensure visibility
        touchTarget.style.height = 'auto';
        touchTarget.style.zIndex = '1000';
        
        // Add visual feedback for the current day
        Object.entries(columnRefs.current).forEach(([dateKey, column]) => {
          if (!column) return;
          
          if (dateKey === currentColumn) {
            column.style.backgroundColor = theme.palette.action.hover;
          } else {
            column.style.backgroundColor = '';
          }
        });
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Clear the long press timer
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    
    // If we're not dragging, it was a regular tap (show details)
    if (!touchDragging) {
      // We'll handle this in the onClick event
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedAppointment) {
      resetTouchDragging();
      return;
    }
    
    // Get the final touch position
    const touchX = e.changedTouches[0].clientX;
    const touchY = e.changedTouches[0].clientY;
    
    // Find which column we ended on
    const finalColumn = findColumnFromPoint(touchX, touchY);
    
    if (finalColumn && columnRefs.current[finalColumn]) {
      // Reset styles on all columns
      Object.values(columnRefs.current).forEach(column => {
        if (column) column.style.backgroundColor = '';
      });
      
      // Reset the dragged element visual state
      if (touchTarget) {
        touchTarget.style.boxShadow = '';
        touchTarget.style.opacity = '';
        touchTarget.style.zIndex = '';
        touchTarget.style.position = '';
        touchTarget.style.width = '';
        touchTarget.style.left = '';
        touchTarget.style.top = '';
        touchTarget.style.height = '';
      }
      
      // Calculate final position
      const time = calculateTimeFromPosition(
        touchY,
        columnRefs.current[finalColumn]!
      );
      
      // Set the drop target with the final column and time
      setDropTarget({ date: finalColumn, time });
      
      // Show confirmation dialog
      resetTouchDragging();
      setDragConfirmOpen(true);
    } else {
      // If we couldn't determine the final column, just reset
      resetTouchDragging();
    }
  };

  const resetTouchDragging = () => {
    setTouchDragging(false);
    setTouchStartY(null);
    setTouchStartX(null);
    setTouchTarget(null);
    setCurrentTouchColumn(null);
  };

  const calculateTimeFromPosition = (y: number, columnElement: HTMLElement): string => {
    // Get column position metrics
    const columnRect = columnElement.getBoundingClientRect();
    
    // Get the height of a time slot based on the current screen size
    const timeSlotHeight = window.innerWidth < 600 ? 100 : 120;
    
    // Calculate position relative to the column content (without header)
    // The first 50px or 40px is the header height
    const headerHeight = window.innerWidth < 600 ? 40 : 50;
    
    // Calculate the offset from the top of the column content
    const offsetY = y - columnRect.top;
    
    // Calculate which time slot we're in (0-based index)
    const timeSlotIndex = Math.floor(offsetY / timeSlotHeight);
    
    // Calculate position within the time slot (0 to 1)
    const positionInTimeSlot = (offsetY % timeSlotHeight) / timeSlotHeight;
    
    // Base hour (9am + slot index)
    let hour = 9 + timeSlotIndex;
    
    // Calculate minutes based on position in time slot
    // Round to nearest 30 minutes for better usability
    let minute = Math.round(positionInTimeSlot * 60 / 30) * 30;
    
    // Handle edge cases
    if (minute === 60) {
      hour += 1;
      minute = 0;
    }
    
    // Cap the time between 9am and 7pm
    hour = Math.max(9, Math.min(19, hour));
    if (hour === 19) minute = 0;
    
    // Format the time string
    const formattedHour = hour.toString().padStart(2, '0');
    const formattedMinute = minute.toString().padStart(2, '0');
    
    return `${formattedHour}:${formattedMinute}:00`;
  };

  const handleDragOver = (e: React.DragEvent, date: string, columnElement: HTMLElement) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedAppointment) return;
    
    // Get column position relative to the viewport
    const columnRect = columnElement.getBoundingClientRect();
    
    // Calculate the Y position relative to the column content (minus header)
    const relativeY = e.clientY - columnRect.top;
    
    // Calculate the time based on mouse position
    const time = calculateTimeFromPosition(e.clientY, columnElement);
    setDropTarget({ date, time });
    
    // Visual feedback - highlight the column where we're dragging over
    Object.entries(columnRefs.current).forEach(([dateKey, column]) => {
      if (!column) return;
      
      if (dateKey === date) {
        column.style.backgroundColor = theme.palette.action.hover;
      } else {
        column.style.backgroundColor = '';
      }
    });
  };

  const handleDrop = (e: React.DragEvent, date: string, columnElement: HTMLElement) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedAppointment) return;
    
    // Reset column backgrounds
    Object.entries(columnRefs.current).forEach(([_, column]) => {
      if (column) column.style.backgroundColor = '';
    });
    
    // Calculate the time based on drop position
    const time = calculateTimeFromPosition(e.clientY, columnElement);
    setDropTarget({ date, time });
    
    // Show confirmation dialog
    setDragConfirmOpen(true);
  };

  const handleConfirmDragReschedule = async () => {
    if (!draggedAppointment || !dropTarget) {
      setDragConfirmOpen(false);
      setDraggedAppointment(null);
      setDropTarget(null);
      return;
    }
    
    setLoading(true);
    try {
      await rescheduleAppointment(draggedAppointment.id, dropTarget.date, dropTarget.time);
      if (onStatusUpdate) {
        onStatusUpdate(draggedAppointment.id, draggedAppointment.status);
      }
      
      if (showNotification) {
        showNotification('Appointment rescheduled successfully', 'success');
      }
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      if (showNotification) {
        showNotification('Failed to reschedule appointment', 'error');
      }
    } finally {
      setLoading(false);
      setDragConfirmOpen(false);
      setDraggedAppointment(null);
      setDropTarget(null);
    }
  };

  const handleCancelDragReschedule = () => {
    setDragConfirmOpen(false);
    setDraggedAppointment(null);
    setDropTarget(null);
  };

  const formatDragConfirmationTime = (time: string) => {
    try {
      return format(parseISO(`2023-01-01T${time}`), 'h:mm a');
    } catch (e) {
      return time;
    }
  };

  // Close appointment details dialog
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setViewingAppointment(null);
  };

  // Register column ref when it mounts
  const registerColumnRef = (dateKey: string, element: HTMLElement | null) => {
    columnRefs.current[dateKey] = element;
  };

  // Determine which days to show - always all 7 days of the week
  const visibleWeekDays = useMemo(() => {
    return weekData.days; // Show all days
  }, [weekData.days]);

  // Calculate the header date range text
  const headerDateText = useMemo(() => {
    // Show full week range
    return `${format(weekData.startDate, 'MMM d')} - ${format(weekData.endDate, 'MMM d, yyyy')}`;
  }, [weekData.startDate, weekData.endDate]);

  // Scroll to today when calendar first loads or when goToToday is called
  const scrollToToday = () => {
    if (daysContainerRef.current) {
      const todayIndex = weekData.days.findIndex(day => isToday(day));
      if (todayIndex >= 0) {
        const columnWidth = daysContainerRef.current.scrollWidth / 7; // Divide by 7 days
        const scrollPosition = todayIndex * columnWidth;
        daysContainerRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
      }
    }
  };

  // Scroll to today on initial render and when date changes
  useEffect(() => {
    if (visibleWeekDays.length) {
      setTimeout(scrollToToday, 100); // Small delay to ensure the DOM is ready
    }
  }, [visibleWeekDays]);

  // Add a scrolling indicator to help users understand they can scroll
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  
  useEffect(() => {
    // Hide the scroll indicator after 3 seconds
    const timer = setTimeout(() => {
      setShowScrollIndicator(false);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Add scroll handlers to allow navigation by scrolling horizontally
  const handleScrollLeft = () => {
    if (daysContainerRef.current) {
      daysContainerRef.current.scrollBy({ left: -150, behavior: 'smooth' });
    }
  };
  
  const handleScrollRight = () => {
    if (daysContainerRef.current) {
      daysContainerRef.current.scrollBy({ left: 150, behavior: 'smooth' });
    }
  };

  // Helper function to calculate end time from start time and duration
  const calculateEndTime = (startTime: string, duration: number = 60): string => {
    const [hour, minute] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hour, minute, 0);
    
    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + duration);
    
    return format(endDate, 'h:mm');
  };

  // Helper function to get appointment color based on status
  const getAppointmentColor = (appointment: Appointment): string => {
    switch (appointment.status) {
      case 'cancelled':
        return 'rgba(244, 67, 54, 0.7)'; // Red with opacity
      case 'completed':
        return 'rgba(76, 175, 80, 0.7)'; // Green with opacity
      case 'no-show':
        return 'rgba(255, 152, 0, 0.7)'; // Orange with opacity
      default:
        return theme.palette.primary.light; // Default color for scheduled
    }
  };

  return (
    <Paper 
      sx={{ 
        borderRadius: 0, 
        width: '100%',
        background: theme.palette.background.paper,
        boxShadow: 'none',
        overflow: 'hidden',
        maxHeight: '90vh',
        position: 'relative',
      }}
    >
      {/* Calendar header */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: { xs: 1, sm: 2 },
          borderBottom: `1px solid rgba(0, 0, 0, 0.1)`,
        }}
      >
        <Box display="flex" alignItems="center">
          <IconButton 
            onClick={goToPreviousWeek} 
            size={isVerySmallScreen ? "small" : "medium"}
          >
            <ChevronLeft />
          </IconButton>
          <Typography 
            variant={isVerySmallScreen ? "subtitle1" : "h6"} 
            sx={{ 
              mx: { xs: 0.5, sm: 1 }, 
              fontWeight: 500,
              fontSize: isVerySmallScreen ? '0.9rem' : undefined,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: { xs: '160px', sm: 'none' },
            }}
          >
            {headerDateText}
          </Typography>
          <IconButton 
            onClick={goToNextWeek} 
            size={isVerySmallScreen ? "small" : "medium"}
          >
            <ChevronRight />
          </IconButton>
        </Box>
        <Button 
          variant="outlined" 
          size={isVerySmallScreen ? "small" : "medium"}
          onClick={goToToday}
          sx={{ fontSize: isVerySmallScreen ? '0.7rem' : undefined }}
        >
          Today
        </Button>
      </Box>

      {/* Hidden element for drag image */}
      <Box
        ref={dragImageRef}
        sx={{
          position: 'absolute',
          top: '-9999px',
          left: '-9999px',
          padding: '8px 12px',
          borderRadius: '4px',
          color: 'white',
          fontSize: '14px',
          fontWeight: 'bold',
          zIndex: -1,
          pointerEvents: 'none',
          display: 'none',
        }}
      />

      {/* Calendar body */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        height: { xs: 'calc(85vh - 60px)', sm: '70vh' }, 
        position: 'relative',
        overflow: 'hidden', // Hide overflow at container level
      }}>
        {/* Day headers row - fixed at top */}
        <Box sx={{ 
          display: 'flex', 
          borderBottom: `1px solid rgba(0, 0, 0, 0.1)`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: theme.palette.background.paper,
          width: '100%',
          overflow: 'hidden',
        }}>
          {/* Empty corner cell */}
          <Box sx={{ 
            width: { xs: '40px', sm: '60px' }, 
            height: { xs: '40px', sm: '50px' },
            borderRight: `1px solid rgba(0, 0, 0, 0.1)`,
            flexShrink: 0,
            zIndex: 2,
            position: 'relative',
            backgroundColor: theme.palette.background.paper,
          }} />

          {/* Day headers - will be synchronized with appointment columns */}
          <Box 
            ref={(el) => {
              // Store the ref in state so we can access it later
              if (el) {
                setHeaderScrollRef(el as HTMLElement);
              }
            }}
            sx={{ 
              display: 'flex',
              flexGrow: 1,
              overflow: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
            className="days-header-scroll"
          >
            <Box 
              sx={{ 
                display: 'flex',
                minWidth: 'fit-content',
              }}
            >
              {visibleWeekDays.map(day => {
                const dateKey = format(day, 'yyyy-MM-dd');
                const isCurrentDay = isToday(day);

                return (
                  <Box 
                    key={`header-${dateKey}`}
                    sx={{ 
                      flex: '0 0 auto',
                      width: { xs: '140px', sm: '160px', md: '180px' }, // Fixed width to match column width
                      height: { xs: '40px', sm: '50px' },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: isCurrentDay 
                        ? theme.palette.primary.light 
                        : theme.palette.background.paper,
                      borderRight: `1px solid rgba(0, 0, 0, 0.1)`,
                      py: { xs: 0.5, sm: 1 },
                    }}
                  >
                    <Typography 
                      variant={isVerySmallScreen ? "caption" : "body2"}
                      sx={{ 
                        fontWeight: 500,
                        fontSize: isVerySmallScreen ? '0.7rem' : undefined,
                      }}
                    >
                      {isVerySmallScreen ? format(day, 'E') : format(day, 'EEE')}
                    </Typography>
                    <Typography 
                      variant={isVerySmallScreen ? "body2" : "body1"}
                      sx={{ 
                        fontWeight: isCurrentDay ? 'bold' : 'normal',
                        color: isCurrentDay ? theme.palette.primary.main : theme.palette.text.primary,
                        fontSize: isVerySmallScreen ? '0.9rem' : undefined,
                      }}
                    >
                      {format(day, 'd')}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Scrollable content area */}
        <Box 
          sx={{ 
            display: 'flex',
            flexGrow: 1,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Time column - Left side */}
          <Box 
            sx={{ 
              width: { xs: '40px', sm: '60px' }, 
              borderRight: `1px solid rgba(0, 0, 0, 0.1)`,
              flexShrink: 0,
              position: 'relative',
              zIndex: 5,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Box
              ref={timeColumnRef}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              {timeSlots.map((time, index) => (
                <Box 
                  key={time} 
                  sx={{ 
                    height: { xs: '100px', sm: '120px' }, // Significantly increased height
                    flexShrink: 0, // Prevent shrinking
                    display: 'flex', 
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    borderTop: index > 0 ? `1px solid rgba(0, 0, 0, 0.1)` : 'none',
                    position: 'relative',
                    px: 0.5,
                  }}
                >
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      position: 'absolute',
                      top: 10, // Consistent position for all time labels
                      left: '50%',
                      transform: 'translateX(-50%)',
                      color: theme.palette.text.secondary,
                      fontSize: isVerySmallScreen ? '0.7rem' : '0.75rem',
                      lineHeight: 1,
                      backgroundColor: theme.palette.background.paper,
                      px: 0.5,
                    }}
                  >
                    {isVerySmallScreen ? time.replace(':00', '') : time}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Scroll indicators (only on mobile) */}
          {isMobile && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  left: { xs: '40px', sm: '60px' },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 20,
                  opacity: showScrollIndicator ? 0.8 : 0,
                  transition: 'opacity 0.5s ease',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={handleScrollLeft}
              >
                <IconButton size="small">
                  <ChevronLeft fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 20,
                  opacity: showScrollIndicator ? 0.8 : 0,
                  transition: 'opacity 0.5s ease',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={handleScrollRight}
              >
                <IconButton size="small">
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            </>
          )}

          {/* Scrollable grid with appointments */}
          <Box 
            ref={daysContainerRef}
            sx={{ 
              flexGrow: 1,
              overflow: 'auto', 
              position: 'relative',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                height: '4px',
                width: '4px'
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.primary.light,
                borderRadius: '2px',
              },
            }}
            onScroll={(e) => {
              // Sync the time column's vertical position
              if (timeColumnRef.current) {
                timeColumnRef.current.style.marginTop = `-${e.currentTarget.scrollTop}px`;
              }
              
              // Sync the day headers' horizontal position
              if (headerScrollRef && !isUserScrolling) {
                setIsUserScrolling(true);
                headerScrollRef.scrollLeft = e.currentTarget.scrollLeft;
                setTimeout(() => setIsUserScrolling(false), 100);
              }
            }}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Days columns with appointments */}
            <Box 
              sx={{ 
                display: 'flex',
                minWidth: 'fit-content',
              }}
            >
              {visibleWeekDays.map(day => {
                const dateKey = format(day, 'yyyy-MM-dd');
                const dayAppointments = appointmentsByDay[dateKey] || [];

                return (
                  <Box 
                    key={dateKey}
                    data-date-key={dateKey}
                    ref={(el) => registerColumnRef(dateKey, el as HTMLElement | null)}
                    sx={{ 
                      flex: '0 0 auto',
                      width: { xs: '140px', sm: '160px', md: '180px' }, // Fixed width to match headers
                      borderRight: `1px solid rgba(0, 0, 0, 0.1)`,
                      position: 'relative',
                      transition: 'background-color 0.2s ease',
                    }}
                    onDragOver={(e) => handleDragOver(e, dateKey, e.currentTarget)}
                    onDrop={(e) => handleDrop(e, dateKey, e.currentTarget)}
                  >
                    {/* Hours grid - increased height */}
                    <Box 
                      sx={{ position: 'relative' }}
                    >
                      {timeSlots.map((_, index) => (
                        <Box 
                          key={index} 
                          sx={{ 
                            height: { xs: '100px', sm: '120px' }, // Significantly increased height
                            borderTop: index > 0 ? `1px solid rgba(0, 0, 0, 0.1)` : 'none',
                          }}
                        />
                      ))}

                      {/* Appointment blocks - Updated with larger size and client info */}
                      {dayAppointments.map(appointment => {
                        const [hour, minute] = appointment.time.split(':').map(Number);
                        const formattedTime = format(new Date().setHours(hour, minute), 'h:mm');
                        const endTime = calculateEndTime(appointment.time, appointment.duration || 60);
                        
                        const topPosition = (hour - 9) * 100 + (minute / 60) * 100; // 9 is the starting hour
                        
                        // Calculate the appointment height based on duration
                        // For a 30-minute appointment, height should be half of the hourly cell
                        const adjustedHeight = ((appointment.duration || 60) / 60) * 100; // Duration in minutes / 60 * cell height
                        
                        return (
                          <Box
                            key={appointment.id}
                            sx={{
                              position: 'absolute',
                              top: { 
                                xs: `${topPosition}px`, 
                                sm: `${(hour - 9) * 120 + (minute / 60) * 120}px` 
                              },
                              left: '3px',
                              right: '3px',
                              height: { 
                                xs: `${adjustedHeight}px`, 
                                sm: `${((appointment.duration || 60) / 60) * 120}px` 
                              },
                              backgroundColor: getAppointmentColor(appointment),
                              borderRadius: '4px',
                              border: `1px solid ${theme.palette.divider}`,
                              padding: { xs: '4px 6px', sm: '6px 8px' },
                              overflow: 'hidden',
                              cursor: 'pointer',
                              zIndex: 1,
                              '&:hover': {
                                boxShadow: 1,
                                zIndex: 2,
                              },
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'flex-start',
                            }}
                            onClick={(event) => handleAppointmentClick(event, appointment)}
                            onTouchStart={(event) => handleTouchStart(event, appointment, event.currentTarget, dateKey)}
                            onTouchEnd={handleTouchEnd}
                            draggable={draggable}
                            onDragStart={(event) => handleDragStart(event, appointment)}
                            onDragEnd={handleDragEnd}
                          >
                            {/* Service name */}
                            <Box sx={{ 
                              mb: 0.25, // Reduced from 0.5
                              lineHeight: 1.2, // Tighter line height
                            }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 'bold',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  fontSize: isVerySmallScreen ? '0.7rem' : '0.8rem',
                                }}
                              >
                                {appointment.service_name}
                              </Typography>
                            </Box>
                            
                            {/* Time and client info */}
                            <Box sx={{ 
                              mt: 0, // Reduced from 0.5
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.25, // Reduced from 0.5
                            }}>
                              {/* Time display */}
                              <Typography
                                variant="caption"
                                sx={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                  px: 0.5,
                                  py: 0.1, // Reduced vertical padding
                                  borderRadius: '2px',
                                  display: 'inline-block',
                                  alignSelf: 'flex-start',
                                  lineHeight: 1.1, // Tighter line height
                                  fontSize: isVerySmallScreen ? '0.65rem' : '0.7rem',
                                }}
                              >
                                {formattedTime} - {endTime}
                              </Typography>
                              
                              {/* Client name */}
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600, // Semi-bold
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  lineHeight: 1.2, // Tighter line height
                                  fontSize: isVerySmallScreen ? '0.65rem' : '0.75rem',
                                }}
                              >
                                {appointment.client_name?.split(' ')[0]}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Appointment action menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { 
            width: isVerySmallScreen ? 180 : 200, 
            mt: 0.5,
            background: theme.palette.background.paper,
          }
        }}
      >
        <MenuItem 
          onClick={handleRescheduleClick}
          sx={{ 
            py: isVerySmallScreen ? 1.5 : 1,
            px: isVerySmallScreen ? 2 : 1,
          }}
        >
          <Typography 
            color="primary"
            variant={isVerySmallScreen ? "body2" : "body1"}
            align="center"
            sx={{ width: '100%' }}
          >
            Reschedule
          </Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusUpdate('completed')}
          sx={{ 
            py: isVerySmallScreen ? 1.5 : 1,
            px: isVerySmallScreen ? 2 : 1,
          }}
        >
          <Typography 
            color="success.main"
            variant={isVerySmallScreen ? "body2" : "body1"}
            align="center"
            sx={{ width: '100%' }}
          >
            Mark as Completed
          </Typography>
        </MenuItem>
        <MenuItem 
          onClick={() => handleStatusUpdate('cancelled')}
          sx={{ 
            py: isVerySmallScreen ? 1.5 : 1,
            px: isVerySmallScreen ? 2 : 1,
          }}
        >
          <Typography 
            color="error.main"
            variant={isVerySmallScreen ? "body2" : "body1"}
            align="center"
            sx={{ width: '100%' }}
          >
            Cancel Appointment
          </Typography>
        </MenuItem>
      </Menu>

      {/* Status update confirmation dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelStatusUpdate}
        PaperProps={{
          sx: {
            width: isVerySmallScreen ? '90%' : 'auto',
            maxWidth: isVerySmallScreen ? '90%' : 'sm',
            background: theme.palette.background.paper,
            borderRadius: isMobile ? 3 : 2,
            position: 'relative',
          }
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              borderRadius: 'inherit',
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}
        <DialogTitle sx={{ 
          color: theme.palette.text.primary,
          fontSize: isMobile ? '1.1rem' : '1.25rem',
        }}>
          {statusToUpdate === 'completed' ? 'Complete Appointment' : 'Cancel Appointment'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.palette.text.secondary }}>
            {statusToUpdate === 'completed'
              ? 'Are you sure you want to mark this appointment as completed?'
              : 'Are you sure you want to cancel this appointment?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: isVerySmallScreen ? 'column' : 'row',
          gap: isVerySmallScreen ? 1 : 0,
          p: 2,
        }}>
          <Button 
            onClick={handleCancelStatusUpdate} 
            color="primary"
            variant="outlined"
            fullWidth={isVerySmallScreen}
            disabled={loading}
          >
            No
          </Button>
          <Button 
            onClick={handleConfirmStatusUpdate} 
            color="primary" 
            variant="contained"
            fullWidth={isVerySmallScreen}
            disabled={loading}
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule dialog */}
      <Dialog
        open={rescheduleDialogOpen}
        onClose={handleRescheduleDialogClose}
        PaperProps={{
          sx: {
            width: isVerySmallScreen ? '90%' : 'auto',
            maxWidth: isVerySmallScreen ? '90%' : 'sm',
            background: theme.palette.background.paper,
            borderRadius: isMobile ? 3 : 2,
            position: 'relative',
          }
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              borderRadius: 'inherit',
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}
        <DialogTitle sx={{ 
          color: theme.palette.text.primary,
          fontSize: isMobile ? '1.1rem' : '1.25rem',
        }}>
          Reschedule Appointment
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ 
            color: theme.palette.text.secondary,
            mb: 2,
          }}>
            {selectedAppointment && `Reschedule appointment for ${selectedAppointment.client_name}`}
          </DialogContentText>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2,
            mt: 1,
          }}>
            <TextField
              label="Date"
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!rescheduleErrors.date}
              helperText={rescheduleErrors.date}
              inputProps={{
                min: format(new Date(), 'yyyy-MM-dd'),
              }}
            />
            
            <FormControl fullWidth error={!!rescheduleErrors.time}>
              <InputLabel id="time-select-label" shrink>Time</InputLabel>
              <Select
                labelId="time-select-label"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                label="Time"
                displayEmpty
              >
                <MenuItem value="" disabled>
                  <em>Select a time</em>
                </MenuItem>
                {generateTimeSlots().map((slot) => (
                  <MenuItem key={slot.value} value={slot.value}>
                    {slot.label}
                  </MenuItem>
                ))}
              </Select>
              {rescheduleErrors.time && <Typography color="error" variant="caption">{rescheduleErrors.time}</Typography>}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: isVerySmallScreen ? 'column' : 'row',
          gap: isVerySmallScreen ? 1 : 0,
          p: 2,
        }}>
          <Button 
            onClick={handleRescheduleDialogClose} 
            color="primary"
            variant="outlined"
            fullWidth={isVerySmallScreen}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmReschedule} 
            color="primary" 
            variant="contained"
            fullWidth={isVerySmallScreen}
            disabled={loading}
          >
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drag reschedule confirmation dialog */}
      <Dialog
        open={dragConfirmOpen}
        onClose={handleCancelDragReschedule}
        PaperProps={{
          sx: {
            width: isVerySmallScreen ? '90%' : 'auto',
            maxWidth: isVerySmallScreen ? '90%' : 'sm',
            background: theme.palette.background.paper,
            borderRadius: isMobile ? 3 : 2,
            position: 'relative',
          }
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              borderRadius: 'inherit',
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        )}
        <DialogTitle sx={{ 
          color: theme.palette.text.primary,
          fontSize: isMobile ? '1.1rem' : '1.25rem',
        }}>
          Reschedule Appointment
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ 
            color: theme.palette.text.secondary,
            mb: 2,
          }}>
            {draggedAppointment && dropTarget && `Reschedule appointment for ${draggedAppointment.client_name} to ${format(parseISO(dropTarget.date), 'MMM d, yyyy')} at ${formatDragConfirmationTime(dropTarget.time)}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: isVerySmallScreen ? 'column' : 'row',
          gap: isVerySmallScreen ? 1 : 0,
          p: 2,
        }}>
          <Button 
            onClick={handleCancelDragReschedule} 
            color="primary"
            variant="outlined"
            fullWidth={isVerySmallScreen}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDragReschedule} 
            color="primary" 
            variant="contained"
            fullWidth={isVerySmallScreen}
            disabled={loading}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Appointment details dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        PaperProps={{
          sx: {
            width: isVerySmallScreen ? '90%' : 'auto',
            maxWidth: isVerySmallScreen ? '90%' : 'sm',
            background: theme.palette.background.paper,
            borderRadius: isMobile ? 3 : 2,
            position: 'relative',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: theme.palette.text.primary,
          fontSize: isMobile ? '1.1rem' : '1.25rem',
        }}>
          Appointment Details
        </DialogTitle>
        <DialogContent>
          {viewingAppointment && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {viewingAppointment.service_name}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Client</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1">{viewingAppointment.client_name}</Typography>
                </Grid>
                
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Date</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1">
                    {format(parseISO(viewingAppointment.date), 'EEEE, MMMM d, yyyy')}
                  </Typography>
                </Grid>
                
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Time</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1">
                    {format(parseISO(`2023-01-01T${viewingAppointment.time}`), 'h:mm a')} - 
                    {format(
                      addHours(
                        parseISO(`2023-01-01T${viewingAppointment.time}`), 
                        (viewingAppointment.duration || 60) / 60
                      ),
                      ' h:mm a'
                    )}
                  </Typography>
                </Grid>
                
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      textTransform: 'capitalize',
                      color: viewingAppointment.status === 'scheduled' 
                        ? 'primary.main' 
                        : viewingAppointment.status === 'completed'
                          ? 'success.main'
                          : 'error.main'
                    }}
                  >
                    {viewingAppointment.status}
                  </Typography>
                </Grid>
              </Grid>
              
              {viewingAppointment.status === 'scheduled' && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  flexDirection: isVerySmallScreen ? 'column' : 'row',
                  gap: 1, 
                  mt: 2 
                }}>
                  <Button 
                    variant="outlined" 
                    color="primary"
                    fullWidth={isVerySmallScreen}
                    onClick={() => {
                      setSelectedAppointment(viewingAppointment);
                      setNewDate(viewingAppointment.date);
                      setNewTime(viewingAppointment.time);
                      handleCloseDetailsDialog();
                      setRescheduleDialogOpen(true);
                    }}
                  >
                    Reschedule
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="success"
                    fullWidth={isVerySmallScreen}
                    onClick={() => {
                      setSelectedAppointment(viewingAppointment);
                      setStatusToUpdate('completed');
                      handleCloseDetailsDialog();
                      setConfirmDialogOpen(true);
                    }}
                  >
                    Complete
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="error"
                    fullWidth={isVerySmallScreen}
                    onClick={() => {
                      setSelectedAppointment(viewingAppointment);
                      setStatusToUpdate('cancelled');
                      handleCloseDetailsDialog();
                      setConfirmDialogOpen(true);
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default NewAppointmentCalendar; 